import React, { useCallback, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import "./index.scss";
import { IconLock, IconLockWhite, IconLoading } from "@icons/index";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { themeActionAtom } from "@src/jotai/theme";
import { useAtom } from "jotai";

export interface Button2Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  children?: React.ReactNode;
  size?: "large" | "middle" | "small" | "mini";
  type?:
    | "primary" // 蓝底
    | "default" // 白底带边框
    | "filled" // 白底带边框hover浅蓝
    | "text" // 文字hover灰
    | "primary-text" // 白底hover浅蓝文字按钮
    | "primary-filled" // 浅蓝底灰字按钮
    | "primary-gradient" // primary 类型的渐变按钮
    | "filled-gradient" // filled 类型的渐变按钮
    | "link" // 链接样式按钮
    | "tag"; // 标签按钮
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  activeIcon?: React.ReactNode; // 激活状态下的图标
  disabledIcon?: React.ReactNode; // 禁用状态下的图标
  subIcon?: React.ReactNode;
  locked?: boolean;
  ghost?: boolean; // 幽灵按钮
  autoPath?: boolean; // 自动设置 SVG path 颜色
  className?: string;
  border?: boolean;
}

export const CommonButton = React.forwardRef<HTMLButtonElement, Button2Props>(
  (
    {
      children,
      size = "middle",
      type = "default",
      disabled = false,
      loading = false,
      icon,
      activeIcon,
      disabledIcon,
      subIcon,
      locked = false,
      ghost = false,
      autoPath = true,
      className = "",
      border = true,
      onClick,
      onMouseEnter,
      onMouseLeave,
      ...restProps
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState(false);
    const [theme] = useAtom(themeActionAtom);
    const isDark = theme === "dark";
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && !loading && !locked && onClick) {
        onClick(e);
      }
    };

    const getSizeClass = () => {
      // 如果没有指定 size，使用 Button 组件的默认样式（py-[3px]）
      if (!size) {
        return "px-[12px] py-[3px]";
      }
      switch (size) {
        case "large":
          return "px-[12px] py-[4px] leading-[32px] h-[40px] text-[16px] font-[500]";
        case "middle":
          return "px-[12px] py-[5px] leading-[22px] h-[32px] text-[14px] font-normal";
        case "small":
          return "px-[12px] py-[2px] leading-[22px] h-[26px] text-[14px] font-normal";
        case "mini":
          return "px-[8px] py-[2px] leading-[16px] h-[20px] text-[12px] font-normal rounded-full! gap-[2px]";
      }
    };

    const getIconSize = useCallback(() => {
      if (["text", "primary-text"].includes(type)) {
        return "w-[16px] h-[16px]";
      }

      switch (size) {
        case "large":
          return "w-[20px] h-[20px]";
        case "middle":
          return "w-[20px] h-[20px]";
        case "small":
        case "mini":
          return "w-[16px] h-[16px]";
      }
    }, [type, size]);

    const getLockedIconSize = () => {
      if (["text", "primary-text"].includes(type)) {
        return "w-[16px] h-[16px]";
      }

      switch (size) {
        case "large":
        case "middle":
          return "w-[20px] h-[20px]";
        case "small":
        case "mini":
          return "w-[16px] h-[16px]";
      }
    };

    const getTypeClass = () => {
      if (ghost) {
        if (disabled || loading || locked) {
          return "CommonButton-disabled bg-transparent text-[#BBB] border-[#EEE] cursor-not-allowed";
        }
        switch (type) {
          case "primary":
            return "text-kds-accent border border-kds-accent hover:bg-filter-option-bg-hover active:border-kds-accent-active active:text-kds-accent-active bg-transparent";
          default:
            return "text-kds-fg-subtle border border-kds-border hover:border-kds-accent hover:text-kds-accent active:border-kds-accent-active active:text-kds-accent-active bg-transparent";
        }
      }

      if (disabled || loading || locked) {
        switch (type) {
          case "primary-filled":
            return "CommonButton-disabled CommonButton-primary-filled";
          case "primary-text":
            return "CommonButton-disabled CommonButton-text";
          case "primary-gradient":
            return "bg-gradient-to-r from-[#0191F8]/50 to-[#1AC3E9]/50 text-white !font-[500] cursor-not-allowed";
          case "filled-gradient":
            return "!font-[500] border-transparent filled-gradient-disabled cursor-not-allowed";
          case "primary":
            return "CommonButton-disabled bg-[#F5F5F5] text-[#BBB] border-transparent cursor-not-allowed";
          case "link":
            return "CommonButton-disabled bg-transparent text-[#BBB] border-transparent cursor-not-allowed";
          case "filled":
            return "CommonButton-disabled CommonButton-filled";
          case "text":
            return "CommonButton-disabled CommonButton-text";
          case "tag":
            return "CommonButton-disabled CommonButton-tag";
          default:
            return "CommonButton-disabled CommonButton-default";
        }
      }

      switch (type) {
        case "primary":
          return "CommonButton-primary";
        case "default":
          return "CommonButton-default";
        case "filled":
          return "CommonButton-filled";
        case "text":
          return "CommonButton-text";
        case "primary-text":
          return isDark ? "CommonButton-primary-text-dark kalo-text-linear-blue" : "CommonButton-primary-text-light";
        case "primary-filled":
          return "CommonButton-primary-filled";
        case "primary-gradient":
          return "bg-gradient-to-r from-[#0191F8] to-[#1AC3E9] text-white !font-[500] active:from-[#0771E3] active:to-[#0771E3]";
        case "filled-gradient":
          return "!font-[500] border-transparent filled-gradient-effect";
        case "tag":
          return "CommonButton-tag";
        default:
          return "CommonButton-default";
      }
    };

    // const getGradientHoverClass = () => {
    //   if (disabled || loading || locked) return "absolute";
    //   switch (type) {
    //     case "primary-gradient":
    //       return "absolute top-[20px] z-1 w-[100px] h-[45px] blur-[15px] bg-[#6D27E780] opacity-0 transition-opacity duration-300 pointer-events-none bg-hover-effect";
    //     case "filled-gradient":
    //       return "absolute top-[20px] z-1 w-[100px] h-[45px] blur-[15px] bg-[#0191F880] opacity-0 transition-opacity duration-300 pointer-events-none bg-hover-effect";
    //     default:
    //       return "absolute";
    //   }
    // };

    const getTextClass = () => {
      if ((disabled || loading || locked) && type === "filled-gradient")
        return "flex items-center bg-gradient-to-r from-[#0191F8]/50 to-[#1AC3E9]/50 bg-clip-text text-transparent";

      switch (type) {
        case "filled-gradient":
          return "flex items-center bg-gradient-to-r from-[#0191F8] to-[#1AC3E9] bg-clip-text text-transparent filled-gradient-text";
        default:
          return "";
      }
    };

    const getLoadingClass = () => {
      switch (type) {
        case "primary-gradient":
          return "loading-white";
        case "filled-gradient":
          return "loading-blue";
        default:
          return "loading";
      }
    };

    // 渲染前置按钮
    const renderPreIcon = useMemo(() => {
      let targetIcon = icon;
      let isCustomDisabledIcon = false;
      if ((disabled || loading || locked) && disabledIcon) {
        targetIcon = disabledIcon;
        isCustomDisabledIcon = true;
      } else if (isActive && activeIcon) {
        targetIcon = activeIcon;
      }

      if (targetIcon) {
        return (
          <span className={!isCustomDisabledIcon ? "CommonButton-icon" : ""}>
            {React.isValidElement(targetIcon)
              ? React.cloneElement(targetIcon as React.ReactElement, {
                  className: twMerge(targetIcon.props?.className, getIconSize()),
                })
              : targetIcon}
          </span>
        );
      }
      return null;
    }, [icon, activeIcon, isActive, disabled, loading, locked, disabledIcon, getIconSize]);

    return (
      <button
        ref={ref}
        {...restProps}
        className={classNames(
          "CommonButton",
          type !== "link" && "border", // link 类型不需要 border
          {
            "border-none!": !border || type === "primary-gradient",
            autoPath: autoPath,
          },
          getSizeClass(),
          getTypeClass(),
          className,
        )}
        onClick={handleClick}
        onMouseEnter={(e) => {
          setIsActive(true);
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setIsActive(false);
          onMouseLeave?.(e);
        }}
        disabled={disabled || loading}
      >
        {/* 渐变 hover */}
        {/* {["primary-gradient", "filled-gradient"].includes(type) && <div className={getGradientHoverClass()} />} */}

        {/* 锁图标 */}
        {locked && (
          <div className="absolute w-full h-full flex items-center justify-center opacity-100! z-10">
            {isDark ? (
              <IconLockWhite {...({ className: getLockedIconSize() } as any)} />
            ) : (
              <IconLock {...({ className: getLockedIconSize() } as any)} />
            )}
          </div>
        )}
        {loading ? (
          <>
            {/* 加载状态 */}
            <IconLoading
              className={classNames("animate-spin CommonButton-icon stroke-0", getLoadingClass(), getIconSize())}
            />
            <span className={getTextClass()}>
              <Trans>Loading</Trans>...
            </span>
          </>
        ) : (
          <>
            {/* 前置图标 */}
            {renderPreIcon}
            {/* 主体内容 */}
            {children && (
              <>{type === "filled-gradient" ? <div className={getTextClass()}>{children}</div> : <>{children}</>}</>
            )}

            {/* 后置图标 */}
            {subIcon && (
              <span className="CommonButton-icon">
                {React.isValidElement(subIcon)
                  ? React.cloneElement(subIcon as React.ReactElement, {
                      className: twMerge(subIcon.props?.className, getIconSize()),
                    })
                  : subIcon}
              </span>
            )}
          </>
        )}
      </button>
    );
  },
);

export default CommonButton;
